<template>
	<v-card color="rgba(41, 41, 47, 0.8)">
		<v-card-title>{{ shapeTitle }}</v-card-title>
		<v-card-text>
			<v-form v-model="isValid">
				<!-- Box Properties -->
				<template v-if="shapeType === 'box'">
					<v-row class="ma-0">
						<v-col cols="12">
							<SolidMeshPropertyFormTable transparent>
								<!-- Name -->
								<tr>
									<td style="width: 40%">{{ $t("NAME") }}</td>
									<td colspan="2">
										<v-text-field variant="underlined" hide-details density="compact" v-model="name" :rules="[
											rules.validName,
											rules.maxLength(100),
											rules.minLength(1),
										]" autofocus />
									</td>
								</tr>
								<!-- Dimensions -->
								<tr @mouseenter="hoveredDimensions = true" @mouseleave="hoveredDimensions = false">
									<td rowspan="3" :class="{ 'row-title': true, 'highlight-title': hoveredDimensions }">{{
										$t("DIMENSIONS")
									}}</td>
									<td class="coordinate-label">{{ $t("X") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="dimensions.x"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" />
									</td>
								</tr>
								<tr @mouseenter="hoveredDimensions = true" @mouseleave="hoveredDimensions = false">
									<td class="coordinate-label">{{ $t("Y") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="dimensions.y"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" />
									</td>
								</tr>
								<tr @mouseenter="hoveredDimensions = true" @mouseleave="hoveredDimensions = false">
									<td class="coordinate-label">{{ $t("Z") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="dimensions.z"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" />
									</td>
								</tr>
								<!-- Anchor Point -->
								<tr @mouseenter="hoveredAnchor = true" @mouseleave="hoveredAnchor = false">
									<td rowspan="3" :class="{ 'row-title': true, 'highlight-title': hoveredAnchor }">{{ $t("ANCHOR_POINT")
									}}</td>
									<td class="coordinate-label">{{ $t("X") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="anchorPoint.x"
											:step="anchorPointStep" :decimal-length="anchorPointDecimals" />
									</td>
								</tr>
								<tr @mouseenter="hoveredAnchor = true" @mouseleave="hoveredAnchor = false">
									<td class="coordinate-label">{{ $t("Y") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="anchorPoint.y"
											:step="anchorPointStep" :decimal-length="anchorPointDecimals" />
									</td>
								</tr>
								<tr @mouseenter="hoveredAnchor = true" @mouseleave="hoveredAnchor = false">
									<td class="coordinate-label">{{ $t("Z") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="anchorPoint.z"
											:step="anchorPointStep" :decimal-length="anchorPointDecimals" />
									</td>
								</tr>
								<!-- Color -->
								<tr>
									<td>{{ $t("COLOR") }}</td>
									<td colspan="2">
										<v-menu>
											<template v-slot:activator="{ props }">
												<v-avatar :color="colorHex" size="20px" v-bind="props" />
											</template>
											<v-color-picker class="ma-0" hide-canvas hide-inputs hide-sliders show-swatches mode="hexa"
												v-model="colorHex" />
										</v-menu>
									</td>
								</tr>
							</SolidMeshPropertyFormTable>
						</v-col>
					</v-row>
				</template>

				<!-- Sphere Properties -->
				<template v-else-if="shapeType === 'sphere'">
					<v-row class="ma-0">
						<v-col cols="12">
							<SolidMeshPropertyFormTable transparent>
								<tr>
									<td style="width: 40%">{{ $t("NAME") }}</td>
									<td colspan="2">
										<v-text-field variant="underlined" hide-details density="compact" v-model="name" :rules="[
											rules.validName,
											rules.maxLength(100),
											rules.minLength(1),
										]" autofocus />
									</td>
								</tr>
								<!-- Diameter (dimensions) -->
								<tr @mouseenter="hoveredDiameter = true" @mouseleave="hoveredDiameter = false">
									<td rowspan="3" :class="{ 'row-title': true, 'highlight-title': hoveredDiameter }">{{ $t("DIAMETER")
									}}
									</td>
									<td class="coordinate-label">{{ $t("X") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="dimensions.x"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" />
									</td>
								</tr>
								<tr @mouseenter="hoveredDiameter = true" @mouseleave="hoveredDiameter = false">
									<td class="coordinate-label">{{ $t("Y") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="dimensions.y"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" />
									</td>
								</tr>
								<tr @mouseenter="hoveredDiameter = true" @mouseleave="hoveredDiameter = false">
									<td class="coordinate-label">{{ $t("Z") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="dimensions.z"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" />
									</td>
								</tr>
								<!-- Anchor Point -->
								<tr @mouseenter="hoveredAnchor = true" @mouseleave="hoveredAnchor = false">
									<td rowspan="3" :class="{ 'row-title': true, 'highlight-title': hoveredAnchor }">{{ $t("ANCHOR_POINT")
									}}</td>
									<td class="coordinate-label">{{ $t("X") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="anchorPoint.x"
											:step="anchorPointStep" :decimal-length="anchorPointDecimals" />
									</td>
								</tr>
								<tr @mouseenter="hoveredAnchor = true" @mouseleave="hoveredAnchor = false">
									<td class="coordinate-label">{{ $t("Y") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="anchorPoint.y"
											:step="anchorPointStep" :decimal-length="anchorPointDecimals" />
									</td>
								</tr>
								<tr @mouseenter="hoveredAnchor = true" @mouseleave="hoveredAnchor = false">
									<td class="coordinate-label">{{ $t("Z") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="anchorPoint.z"
											:step="anchorPointStep" :decimal-length="anchorPointDecimals" />
									</td>
								</tr>
								<!-- Color -->
								<tr>
									<td>{{ $t("COLOR") }}</td>
									<td colspan="2">
										<v-menu>
											<template v-slot:activator="{ props }">
												<v-avatar :color="colorHex" size="20px" v-bind="props" />
											</template>
											<v-color-picker class="ma-0" hide-canvas hide-inputs hide-sliders show-swatches mode="hexa"
												v-model="colorHex" />
										</v-menu>
									</td>
								</tr>
							</SolidMeshPropertyFormTable>
						</v-col>
					</v-row>
				</template>

				<!-- Cylinder Properties -->
				<template v-else-if="shapeType === 'cylinder'">
					<v-row class="ma-0">
						<v-col cols="12">
							<SolidMeshPropertyFormTable transparent>
								<tr>
									<td style="width: 40%">{{ $t("NAME") }}</td>
									<td colspan="2">
										<v-text-field variant="underlined" hide-details density="compact" v-model="name" :rules="[
											rules.validName,
											rules.maxLength(100),
											rules.minLength(1),
										]" autofocus />
									</td>
								</tr>
								<!-- Diameter (X/Y) + Height (Z) -->
								<tr @mouseenter="hoveredDiameter = true" @mouseleave="hoveredDiameter = false">
									<td rowspan="2" :class="{ 'row-title': true, 'highlight-title': hoveredDiameter }">{{ $t("DIAMETER")
									}}
									</td>
									<td class="coordinate-label">{{ $t("X") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="dimensions.x"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" />
									</td>
								</tr>
								<tr @mouseenter="hoveredDiameter = true" @mouseleave="hoveredDiameter = false">
									<td class="coordinate-label">{{ $t("Y") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="dimensions.y"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" />
									</td>
								</tr>
								<tr @mouseenter="hoveredHeight = true" @mouseleave="hoveredHeight = false">
									<td :class="{ 'row-title': true, 'highlight-title': hoveredHeight }">{{ $t("DIMENSION") }}</td>
									<td class="coordinate-label">{{ $t("Z") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="dimensions.z"
											:step="SIMPLE_STRUCTURE_DIMENSION_MIN" :min-value="SIMPLE_STRUCTURE_DIMENSION_MIN"
											:max-value="SIMPLE_STRUCTURE_DIMENSION_MAX" :decimal-length="DIMENSION_DECIMALS" />
									</td>
								</tr>
								<!-- Anchor Point -->
								<tr @mouseenter="hoveredAnchor = true" @mouseleave="hoveredAnchor = false">
									<td rowspan="3" :class="{ 'row-title': true, 'highlight-title': hoveredAnchor }">{{ $t("ANCHOR_POINT")
									}}</td>
									<td class="coordinate-label">{{ $t("X") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="anchorPoint.x"
											:step="anchorPointStep" :decimal-length="anchorPointDecimals" />
									</td>
								</tr>
								<tr @mouseenter="hoveredAnchor = true" @mouseleave="hoveredAnchor = false">
									<td class="coordinate-label">{{ $t("Y") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="anchorPoint.y"
											:step="anchorPointStep" :decimal-length="anchorPointDecimals" />
									</td>
								</tr>
								<tr @mouseenter="hoveredAnchor = true" @mouseleave="hoveredAnchor = false">
									<td class="coordinate-label">{{ $t("Z") }}</td>
									<td>
										<DistanceSettingInput variant="underlined" hide-details density="compact" v-model="anchorPoint.z"
											:step="anchorPointStep" :decimal-length="anchorPointDecimals" />
									</td>
								</tr>
								<!-- Color -->
								<tr>
									<td>{{ $t("COLOR") }}</td>
									<td colspan="2">
										<v-menu>
											<template v-slot:activator="{ props }">
												<v-avatar :color="colorHex" size="20px" v-bind="props" />
											</template>
											<v-color-picker class="ma-0" hide-canvas hide-inputs hide-sliders show-swatches mode="hexa"
												v-model="colorHex" />
										</v-menu>
									</td>
								</tr>
							</SolidMeshPropertyFormTable>
						</v-col>
					</v-row>
				</template>
			</v-form>
		</v-card-text>
		<v-card-actions class="justify-end pa-4">
			<v-btn variant="flat" @click="cancelSolidMesh" color="red">
				{{ $t("CANCEL") }}
			</v-btn>
			<v-btn variant="flat" @click="addSolidMesh" color="primary" :disabled="!isValid">
				{{ $t("ADD") }}
			</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import { SolidMeshObject } from "@/models/objects/solidMesh/SolidMeshObject";
import { ObjectType } from "@/models/objects/types";
import { SIMPLE_STRUCTURE_TYPE } from "@/models/objects/solidMesh/types";
import { notifyError, notifySuccess } from "@/utils/notificationUtils";
import {
	GoogleAnalyticsClickTypes,
	addClickTypeLog,
} from "../../../utils/googleAnalytics.js";
import {
	SIMPLE_STRUCTURE_EDIT_CONSTRAINTS,
	SIMPLE_STRUCTURE_DIMENSION_MAX,
	SIMPLE_STRUCTURE_DIMENSION_MIN,
} from "./composables/useSolidMeshEdit";
import DistanceSettingInput from "../DistanceSettingInput.vue";
import SolidMeshPropertyFormTable from "./SolidMeshPropertyFormTable.vue";
import { DIMENSION_DECIMALS } from "./composables/useSolidMeshDimensionConfig";

export default {
	name: "PropertyDialogSolidMesh",
	components: { SolidMeshPropertyFormTable, DistanceSettingInput },
	props: {
		id: String,
		type: Number,
	},
	data: () => ({
		name: "",
		dimensions: { x: 0, y: 0, z: 0 },
		anchorPoint: { x: 0, y: 0, z: 0 },
		colorHex: "#80CCFF",
		isValid: true,
		rules: {
			validName: (v) => !!v || "Name is required",
			maxLength: (max) => (v) =>
				(v && v.length <= max) || `Max ${max} characters`,
			minLength: (min) => (v) =>
				(v && v.length >= min) || `Min ${min} characters`,
		},
		// hover states for rowspan title highlighting
		hoveredDimensions: false,
		hoveredDiameter: false,
		hoveredHeight: false,
		hoveredAnchor: false,
		// Timer for debounced preview updates
		previewUpdateTimer: null,
		// Version counter to discard stale preview results
		previewVersion: 0,
		editingSolidMesh: null,
	}),
	computed: {
		DIMENSION_DECIMALS() {
			return DIMENSION_DECIMALS;
		},
		SIMPLE_STRUCTURE_DIMENSION_MAX() {
			return SIMPLE_STRUCTURE_DIMENSION_MAX;
		},
		SIMPLE_STRUCTURE_DIMENSION_MIN() {
			return SIMPLE_STRUCTURE_DIMENSION_MIN;
		},
		anchorPointStep() {
			return SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.step;
		},
		anchorPointDecimals() {
			return SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.decimalLength;
		},
		solidMesh() {
			return this.editingSolidMesh;
		},
		shapeType() {
			return this.solidMesh?.metaData?.shapeConfig?.shapeType || "box";
		},
		shapeTitle() {
			const keyMap = {
				box: "ADD_CUBE",
				sphere: "ADD_SPHERE",
				cylinder: "ADD_CYLINDER",
			};
			const key = keyMap[this.shapeType];
			return key ? this.$t(key) : this.$t("ADD_MANUAL");
		},
	},
	mounted() {
		this.loadSolidMeshData();
	},
	watch: {
		// Real-time preview for dimensions
		dimensions: {
			handler() {
				if (this.solidMesh?.isLoaded) {
					this.schedulePreviewUpdate();
				}
			},
			deep: true,
		},
		// Real-time preview for anchor point
		anchorPoint: {
			handler() {
				if (this.solidMesh?.isLoaded) {
					this.schedulePreviewUpdate();
				}
			},
			deep: true,
		},
		// Real-time preview for color
		colorHex() {
			if (this.solidMesh?.isLoaded) {
				this.schedulePreviewUpdate();
			}
		},
	},
	methods: {
		resolveSolidMesh() {
			const objectInStore = this.$store.state.object.objects.find(
				(obj) => obj.type === ObjectType.SOLID_MESH && obj.id === this.id,
			);
			if (objectInStore) {
				this.editingSolidMesh = objectInStore;
				return objectInStore;
			}

			if (!this.id) {
				this.editingSolidMesh = null;
				return null;
			}

			try {
				const viewer = this.$store.getters.viewer;
				const tempSolidMesh = SolidMeshObject.createFromViewer(
					viewer,
					this.id,
					{ structureType: SIMPLE_STRUCTURE_TYPE.MANUAL },
				);
				this.editingSolidMesh = tempSolidMesh;
				return tempSolidMesh;
			} catch (error) {
				console.warn(
					"PropertyDialogSolidMesh: failed to resolve temporary solid mesh",
					error,
				);
				this.editingSolidMesh = null;
				return null;
			}
		},
		resetProperty() {
			this.resolveSolidMesh();
			this.loadSolidMeshData();
		},
		loadSolidMeshData() {
			const solidMesh = this.resolveSolidMesh();
			if (!solidMesh) {
				console.warn("PropertyDialogSolidMesh: solidMesh not found in store");
				return;
			}

			const shapeConfig = solidMesh.metaData?.shapeConfig;
			if (!shapeConfig) {
				console.warn("PropertyDialogSolidMesh: shapeConfig not found");
				return;
			}

			this.name = solidMesh.name;
			const shapeType = shapeConfig.shapeType;

			// Box specific
			if (shapeType === "box") {
				if (shapeConfig.dimensions) {
					this.dimensions = {
						// Kept unrounded; the input owns the displayed digits.
						x: Number(shapeConfig.dimensions.x),
						y: Number(shapeConfig.dimensions.y),
						z: Number(shapeConfig.dimensions.z),
					};
				}
				// Calculate anchor from localCenter + dimensions
				if (shapeConfig.localCenter && shapeConfig.dimensions) {
					this.anchorPoint = {
						x: Number(
							shapeConfig.localCenter.x - shapeConfig.dimensions.x * 0.5,
						).toFixed(3),
						y: Number(
							shapeConfig.localCenter.y + shapeConfig.dimensions.y * 0.5,
						).toFixed(3),
						z: Number(
							shapeConfig.localCenter.z - shapeConfig.dimensions.z * 0.5,
						).toFixed(3),
					};
				}
				if (shapeConfig.color) {
					this.colorHex = this.rgbToHex(
						shapeConfig.color.red,
						shapeConfig.color.green,
						shapeConfig.color.blue,
					);
				}
			}

			// Sphere specific
			if (shapeType === "sphere") {
				if (shapeConfig.dimensions) {
					this.dimensions = {
						// Kept unrounded; the input owns the displayed digits.
						x: Number(shapeConfig.dimensions.x),
						y: Number(shapeConfig.dimensions.y),
						z: Number(shapeConfig.dimensions.z),
					};
				}
				// Calculate anchor from localCenter + dimensions
				if (shapeConfig.localCenter && shapeConfig.dimensions) {
					this.anchorPoint = {
						x: Number(
							shapeConfig.localCenter.x - shapeConfig.dimensions.x * 0.5,
						).toFixed(3),
						y: Number(
							shapeConfig.localCenter.y + shapeConfig.dimensions.y * 0.5,
						).toFixed(3),
						z: Number(
							shapeConfig.localCenter.z - shapeConfig.dimensions.z * 0.5,
						).toFixed(3),
					};
				}
				if (shapeConfig.color) {
					this.colorHex = this.rgbToHex(
						shapeConfig.color.red,
						shapeConfig.color.green,
						shapeConfig.color.blue,
					);
				}
			}

			// Cylinder specific
			if (shapeType === "cylinder") {
				if (shapeConfig.dimensions) {
					this.dimensions = {
						// Kept unrounded; the input owns the displayed digits.
						x: Number(shapeConfig.dimensions.x),
						y: Number(shapeConfig.dimensions.y),
						z: Number(shapeConfig.dimensions.z),
					};
				}
				// Calculate anchor from localCenter + dimensions
				if (shapeConfig.localCenter && shapeConfig.dimensions) {
					this.anchorPoint = {
						x: Number(
							shapeConfig.localCenter.x - shapeConfig.dimensions.x * 0.5,
						).toFixed(3),
						y: Number(
							shapeConfig.localCenter.y + shapeConfig.dimensions.y * 0.5,
						).toFixed(3),
						z: Number(
							shapeConfig.localCenter.z - shapeConfig.dimensions.z * 0.5,
						).toFixed(3),
					};
				}
				if (shapeConfig.color) {
					this.colorHex = this.rgbToHex(
						shapeConfig.color.red,
						shapeConfig.color.green,
						shapeConfig.color.blue,
					);
				}
			}
		},
		rgbToHex(r, g, b) {
			const toHex = (v) => {
				const hex = Math.round(v * 255).toString(16);
				return hex.length === 1 ? `0${hex}` : hex;
			};
			return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
		},
		hexToRgb(hex) {
			const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
			return result
				? {
						red: Number.parseInt(result[1], 16) / 255,
						green: Number.parseInt(result[2], 16) / 255,
						blue: Number.parseInt(result[3], 16) / 255,
					}
				: { red: 0.5, green: 0.8, blue: 1.0 };
		},
		async addSolidMesh() {
			addClickTypeLog(
				GoogleAnalyticsClickTypes.P_CLICK_ADD_SIMPLE_STRUCTURE_MANUAL,
			);
			const solidMesh = this.resolveSolidMesh();
			if (!solidMesh || !this.name) return;

			try {
				// Update dimensions in metadata
				solidMesh.metaData.shapeConfig.dimensions = {
					x: Number(this.dimensions.x),
					y: Number(this.dimensions.y),
					z: Number(this.dimensions.z),
				};

				// Rebuild mesh with new dimensions by reloading
				const viewer = this.$store.getters.viewer;
				await viewer.loadSolidMeshJSON({
					id: solidMesh.id,
					shapeConfig: solidMesh.metaData.shapeConfig,
					attributes: {
						anchorType: solidMesh.metaData.attributes.anchorType,
					},
				});

				// Update name
				solidMesh.setName(this.name);

				// Update color
				const rgb = this.hexToRgb(this.colorHex);
				await solidMesh.setColor(rgb);

				// Update position (anchor based on anchorType)
				await solidMesh.setLocalPosition({
					x: Number(this.anchorPoint.x),
					y: Number(this.anchorPoint.y),
					z: Number(this.anchorPoint.z),
				});

				if (
					!this.$store.state.object.objects.some(
						(obj) => obj.id === solidMesh.id,
					)
				) {
					this.$store.commit("object/addObject", solidMesh);
				}

				// Save to server
				const siteId = this.$route.query.siteId;
				await this.$store.dispatch("object/createObj", {
					siteId,
					obj: solidMesh,
				});
				notifySuccess(
					{ $store: this.$store },
					`${this.$t("ADD_SIMPLE_STRUCTURE")} ${this.$t("successful")}`,
				);
			} catch (error) {
				console.error("Failed to save solid mesh:", error);
				// Clean up: remove from viewer and store (not saved to server yet)
				solidMesh.deleteObject();
				this.$store.commit("object/removeObject", solidMesh.id);
				notifyError(
					{ $store: this.$store },
					`${this.$t("ADD_SIMPLE_STRUCTURE")} ${this.$t("failed")}`,
				);
			}
			this.$emit("close");
		},
		async cancelSolidMesh() {
			const solidMesh = this.resolveSolidMesh();
			if (solidMesh) {
				const solidMeshId = solidMesh.id;
				try {
					solidMesh.deleteObject();
					if (
						this.$store.state.object.objects.some(
							(obj) => obj.id === solidMeshId,
						)
					) {
						this.$store.commit("object/removeObject", solidMeshId);
					}
				} catch (error) {
					console.warn("[SolidMesh] cancel cleanup failed:", error);
				}
			}
			this.$emit("close");
		},
		/**
		 * Schedule a debounced preview update
		 */
		schedulePreviewUpdate() {
			// Clear existing timer
			if (this.previewUpdateTimer) {
				clearTimeout(this.previewUpdateTimer);
			}
			// Schedule new update after 300ms
			this.previewUpdateTimer = setTimeout(() => {
				this.updatePreview();
			}, 300);
		},
		/**
		 * Update the mesh preview with current parameters
		 */
		async updatePreview() {
			const solidMesh = this.solidMesh;
			if (!solidMesh || !solidMesh.isLoaded) {
				return;
			}

			const version = ++this.previewVersion;

			try {
				const viewer = this.$store.getters.viewer;

				// Update dimensions in metadata
				solidMesh.metaData.shapeConfig.dimensions = {
					x: Number(this.dimensions.x),
					y: Number(this.dimensions.y),
					z: Number(this.dimensions.z),
				};

				// Rebuild mesh with new dimensions
				await viewer.loadSolidMeshJSON({
					id: solidMesh.id,
					shapeConfig: solidMesh.metaData.shapeConfig,
					attributes: {
						anchorType: solidMesh.metaData.attributes.anchorType,
					},
				});

				// Discard if a newer preview was requested during the await
				if (this.previewVersion !== version) return;

				// Update color
				const rgb = this.hexToRgb(this.colorHex);
				await viewer.setSolidMeshColor(solidMesh.id, [
					rgb.red,
					rgb.green,
					rgb.blue,
				]);

				// Update position
				await viewer.setLocalPosition(solidMesh.id, [
					Number(this.anchorPoint.x),
					Number(this.anchorPoint.y),
					Number(this.anchorPoint.z),
				]);

				// Ensure visibility
				viewer.setSolidMeshVisibility(solidMesh.id, true);
			} catch (error) {
				console.error("Failed to update mesh preview:", error);
			}
		},
	},
	beforeUnmount() {
		// Clean up the preview update timer
		if (this.previewUpdateTimer) {
			clearTimeout(this.previewUpdateTimer);
			this.previewUpdateTimer = null;
		}
	},
};
</script>
